import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaPaperPlane, FaClock, FaSave, FaPlus, FaTrash, FaComments } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import AlertModal from '../AlertModal/AlertModal';
import './ConfigurationModal.scss';

const defaultReminderSchedules = [
    { id: 1, value: 1, unit: 'days', enabled: true },
    { id: 2, value: 2, unit: 'days', enabled: true }
];

const normalizeReminderSchedules = (schedules = defaultReminderSchedules) => {
    const sourceSchedules = Array.isArray(schedules) && schedules.length > 0 ? schedules : defaultReminderSchedules;
    const seenKeys = new Set();

    const normalized = sourceSchedules.reduce((acc, schedule, index) => {
        const rawValue = parseInt(schedule?.value, 10);
        if (!Number.isInteger(rawValue) || rawValue < 1) {
            return acc;
        }

        const normalizedValue = schedule?.unit === 'hours'
            ? Math.max(1, Math.ceil(rawValue / 24))
            : rawValue;
        const normalizedSchedule = {
            id: schedule?.id || index + 1,
            value: normalizedValue,
            unit: 'days',
            enabled: schedule?.enabled !== false
        };
        const dedupeKey = `${normalizedSchedule.value}_${normalizedSchedule.unit}`;

        if (seenKeys.has(dedupeKey)) {
            return acc;
        }

        seenKeys.add(dedupeKey);
        acc.push(normalizedSchedule);
        return acc;
    }, []);

    return normalized.length > 0 ? normalized : defaultReminderSchedules;
};

const ConfigurationModal = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('test');
    const [loading, setLoading] = useState(false);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [cleanupTarget, setCleanupTarget] = useState(null);
    const configurationHeading = useRef(null);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);
    const [cleanupResult, setCleanupResult] = useState(null);
    const [error, setError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Reminder configuration - array of reminder schedules
    const [reminderSchedules, setReminderSchedules] = useState(defaultReminderSchedules);
    const [nextId, setNextId] = useState(3);
    const [reminderSendTime, setReminderSendTime] = useState('15:00'); // Default 3 PM
    const [reminderSenderPhone, setReminderSenderPhone] = useState('');
    const [selectedReminderPromptId, setSelectedReminderPromptId] = useState('');
    const [useAiPersonalization, setUseAiPersonalization] = useState(true);
    const [maxMessageCharacters, setMaxMessageCharacters] = useState(250); // Default max chars for AI messages
    const [appointmentSyncEnabled, setAppointmentSyncEnabled] = useState(true); // Enable daily sync by default
    const [sendPlanCreationEnabled, setSendPlanCreationEnabled] = useState(true);

    // Service prompts configuration
    const [servicePrompts, setServicePrompts] = useState({
        systemDefault: 'Just a quick reminder that your {service} appointment is set for {date} at {time}. If the weather could affect the visit, include that naturally and make RESCHEDULE feel easy.',
        services: [
            { id: 1, name: 'Pest Control', prompt: 'Reminder: Your pest control service is scheduled for {date} at {time}. We\'ll keep those bugs away!' },
            { id: 2, name: 'Lawn Care', prompt: 'Your lawn care appointment is coming up on {date} at {time}. Let\'s keep your lawn looking great!' }
        ]
    });
    const [nextServiceId, setNextServiceId] = useState(3);

    // FieldRoutes service types (from API)
    const [fieldroutesServiceTypes, setFieldroutesServiceTypes] = useState([]);

    // Load configuration on mount
    useEffect(() => {
        if (isOpen) {
            loadConfiguration();
        } else {
            setCleanupTarget(null);
        }
    }, [isOpen]);

    const loadConfiguration = async () => {
        try {
            // Load reminder schedules
            const reminderResponse = await fetch('/api/config/reminder-schedules');
            const reminderData = await reminderResponse.json();

            if (reminderData.success && reminderData.data && reminderData.data.schedules) {
                const normalizedSchedules = normalizeReminderSchedules(reminderData.data.schedules);
                setReminderSchedules(normalizedSchedules);
                const maxId = Math.max(...normalizedSchedules.map(s => s.id), 0);
                setNextId(maxId + 1);
                // Load send time if available
                if (reminderData.data.sendTime || reminderData.data.syncTime) {
                    setReminderSendTime(reminderData.data.sendTime || reminderData.data.syncTime);
                }
                if (reminderData.data.senderPhone !== undefined) {
                    setReminderSenderPhone(reminderData.data.senderPhone || '');
                }
                if (reminderData.data.selectedPromptId !== undefined && reminderData.data.selectedPromptId !== null) {
                    setSelectedReminderPromptId(String(reminderData.data.selectedPromptId));
                } else {
                    setSelectedReminderPromptId('');
                }
                // Load AI personalization setting
                if (reminderData.data.useAiPersonalization !== undefined) {
                    setUseAiPersonalization(Boolean(reminderData.data.useAiPersonalization));
                }
                // Load max message characters setting
                if (reminderData.data.maxMessageCharacters !== undefined) {
                    setMaxMessageCharacters(reminderData.data.maxMessageCharacters);
                }
                // Load appointment sync enabled setting
                if (reminderData.data.appointmentSyncEnabled !== undefined) {
                    setAppointmentSyncEnabled(reminderData.data.appointmentSyncEnabled);
                }
                if (reminderData.data.sendPlanCreationEnabled !== undefined) {
                    setSendPlanCreationEnabled(reminderData.data.sendPlanCreationEnabled);
                }
            }

            // Load service prompts
            const promptsResponse = await fetch('/api/config/service-prompts');
            const promptsData = await promptsResponse.json();

            if (promptsData.success && promptsData.data) {
                setServicePrompts(promptsData.data);
                if (promptsData.data.services && promptsData.data.services.length > 0) {
                    const maxServiceId = Math.max(...promptsData.data.services.map(s => s.id), 0);
                    setNextServiceId(maxServiceId + 1);
                }
            }

            // Load FieldRoutes service types
            try {
                const serviceTypesResponse = await fetch('/api/fieldroutes/service-types');
                const serviceTypesData = await serviceTypesResponse.json();
                if (serviceTypesData.success && serviceTypesData.data) {
                    setFieldroutesServiceTypes(serviceTypesData.data);
                }
            } catch (err) {
                console.log('FieldRoutes service types not available:', err.message);
            }
        } catch (err) {
            console.error('Error loading configuration:', err);
        }
    };

    // Test message form
    const getDefaultAppointmentDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        // Format as MM/DD/YYYY for US format
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrow.getDate()).padStart(2, '0');
        const year = tomorrow.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const [testFormData, setTestFormData] = useState({
        recipientPhone: '5189193221',
        recipientFirstName: '',
        recipientLastName: '',
        appointmentDate: getDefaultAppointmentDate(),
        serviceId: '', // Selected service ID
        customServiceDescription: '', // Custom service (like from FieldRoutes)
        city: 'Albany',
        state: 'NY',
        zipCode: '12203'
    });

    const handleTestFormChange = (e) => {
        const { name, value } = e.target;
        setTestFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
        setResult(null);
        setCleanupResult(null);
    };

    // Reminder schedule handlers
    const addReminderSchedule = () => {
        setReminderSchedules([...reminderSchedules, {
            id: nextId,
            value: 1,
            unit: 'days',
            enabled: true
        }]);
        setNextId(nextId + 1);
        setSaveSuccess(false);
    };

    const removeReminderSchedule = (id) => {
        setReminderSchedules(reminderSchedules.filter(s => s.id !== id));
        setSaveSuccess(false);
    };

    const updateReminderSchedule = (id, field, value) => {
        setReminderSchedules(reminderSchedules.map(s =>
            s.id === id ? { ...s, [field]: value } : s
        ));
        setSaveSuccess(false);
    };

    // Service prompt handlers
    const addServicePrompt = () => {
        setServicePrompts({
            ...servicePrompts,
            services: [...servicePrompts.services, {
                id: nextServiceId,
                name: '',
                prompt: ''
            }]
        });
        setNextServiceId(nextServiceId + 1);
        setSaveSuccess(false);
    };

    const removeServicePrompt = (id) => {
        setServicePrompts({
            ...servicePrompts,
            services: servicePrompts.services.filter(s => s.id !== id)
        });
        setSaveSuccess(false);
    };

    const updateServicePrompt = (id, field, value) => {
        setServicePrompts({
            ...servicePrompts,
            services: servicePrompts.services.map(s =>
                s.id === id ? { ...s, [field]: value } : s
            )
        });
        setSaveSuccess(false);
    };

    const updateSystemDefault = (value) => {
        setServicePrompts({
            ...servicePrompts,
            systemDefault: value
        });
        setSaveSuccess(false);
    };

    const handleSaveConfiguration = async () => {
        try {
            setSaving(true);
            setError('');

            // Save reminder schedules
            const reminderResponse = await fetch('/api/config/reminder-schedules', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    schedules: reminderSchedules,
                    sendTime: reminderSendTime,
                    syncTime: reminderSendTime,
                    senderPhone: reminderSenderPhone,
                    selectedPromptId: selectedReminderPromptId ? parseInt(selectedReminderPromptId, 10) : null,
                    useAiPersonalization: true,
                    maxMessageCharacters: maxMessageCharacters,
                    appointmentSyncEnabled: appointmentSyncEnabled,
                    sendPlanCreationEnabled: sendPlanCreationEnabled
                })
            });

            const reminderData = await reminderResponse.json();

            if (!reminderResponse.ok || !reminderData.success) {
                throw new Error(reminderData.message || 'Failed to save reminder schedules');
            }

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);

        } catch (err) {
            console.error('Error saving configuration:', err);
            setError(err.message || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveServicePrompts = async () => {
        try {
            setSaving(true);
            setError('');

            const response = await fetch('/api/config/service-prompts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(servicePrompts)
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to save service prompts');
            }

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);

        } catch (err) {
            console.error('Error saving service prompts:', err);
            setError(err.message || 'Failed to save service prompts');
        } finally {
            setSaving(false);
        }
    };

    const handleTestSubmit = async (e) => {
        e.preventDefault();

        if (!testFormData.recipientPhone || !testFormData.recipientFirstName) {
            setError('Recipient phone and first name are required');
            return;
        }

        // If a FieldRoutes service is selected, customServiceDescription will be auto-filled
        if (!testFormData.serviceId && !testFormData.customServiceDescription) {
            setError('Please select a service or enter a custom service description');
            return;
        }

        // Validation passes if either a service is selected OR custom description is provided

        if (!testFormData.city || !testFormData.state || !testFormData.zipCode) {
            setError('Location information (city, state, ZIP code) is required for weather-aware reminders');
            return;
        }

        try {
            setLoading(true);
            setError('');
            setResult(null);
            setCleanupResult(null);
            const locationId = new URLSearchParams(window.location.search).get('location');

            // Get service description from custom field or selected service
            const selectedService = servicePrompts.services.find(s => s.id === parseInt(testFormData.serviceId));
            const serviceDescription = testFormData.customServiceDescription || selectedService?.name || 'Test Service';

            // Call the immediate reminder test endpoint (full flow)
            const response = await fetch('/api/admin/test/immediate-reminder', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    phone: testFormData.recipientPhone,
                    firstName: testFormData.recipientFirstName,
                    lastName: testFormData.recipientLastName,
                    serviceDescription: serviceDescription,
                    city: testFormData.city,
                    state: testFormData.state,
                    zipCode: testFormData.zipCode,
                    locationId
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to schedule test reminder');
            }

            console.log('✅ Test reminder scheduled successfully');
            setResult({
                ...data.data,
                isFullFlowTest: true
            });

        } catch (err) {
            console.error('Error scheduling test reminder:', err);
            setError(err.message || 'Failed to schedule test reminder');
        } finally {
            setLoading(false);
        }
    };

    const requestCleanupTestData = () => {
        if (!testFormData.recipientPhone) {
            setError('Recipient phone is required to clean up test data');
            return;
        }

        setCleanupTarget({
            phone: testFormData.recipientPhone,
            locationId: new URLSearchParams(window.location.search).get('location')
        });
    };

    const handleCleanupTestData = async () => {
        if (!cleanupTarget) return;

        try {
            setCleanupLoading(true);
            setError('');
            setCleanupResult(null);

            const response = await fetch('/api/admin/test/cleanup-immediate-reminders', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(cleanupTarget)
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to clean up test data');
            }

            setResult(null);
            setCleanupResult(data.data);
        } catch (err) {
            console.error('Error cleaning up test data:', err);
            throw err;
        } finally {
            setCleanupLoading(false);
        }
    };

    const handleClose = () => {
        if (cleanupLoading) return;
        setCleanupTarget(null);
        setTestFormData({
            recipientPhone: '5189193221',
            recipientFirstName: '',
            recipientLastName: '',
            appointmentDate: getDefaultAppointmentDate(),
            serviceId: '',
            customServiceDescription: '',
            city: 'Albany',
            state: 'NY',
            zipCode: '12203'
        });
        setResult(null);
        setCleanupResult(null);
        setError('');
        setSaveSuccess(false);
        setActiveTab('test');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
        <AlertModal
            isOpen={Boolean(cleanupTarget)}
            onClose={() => setCleanupTarget(null)}
            onConfirm={handleCleanupTestData}
            title="Delete test conversations?"
            message={`Delete test reminders and conversations for ${cleanupTarget?.phone || ''}?`}
            confirmText="Delete test data"
            pendingText="Deleting..."
            variant="danger"
            returnFocusRef={configurationHeading}
        />
        <div className="configuration-modal-overlay" onClick={handleClose}>
            <div className="configuration-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 ref={configurationHeading} tabIndex={-1}>System Configuration</h2>
                    <button className="close-btn" onClick={handleClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-tabs">
                    <button
                        className={`tab ${activeTab === 'test' ? 'active' : ''}`}
                        onClick={() => setActiveTab('test')}
                    >
                        <FaPaperPlane />
                        Send Message
                    </button>
                    <button
                        className={`tab ${activeTab === 'reminders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reminders')}
                    >
                        <FaClock />
                        Reminder Schedule
                    </button>
                    <button
                        className={`tab ${activeTab === 'prompts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('prompts')}
                    >
                        <FaComments />
                        Service Prompts
                    </button>
                </div>

                <div className="modal-body">
                    {error && (
                        <div className="error-message">
                            <p>❌ {error}</p>
                        </div>
                    )}

                    {saveSuccess && (
                        <div className="success-message">
                            <p>✅ Configuration saved successfully!</p>
                        </div>
                    )}

                    {/* Reminder Configuration Tab */}
                    {activeTab === 'reminders' && (
                        <div className="config-section">
                            <h3>Reminder Schedule Configuration</h3>
                            <p className="section-description">
                                Configure how many days out reminders should go. At the configured daily time, Promptly pulls the latest appointments and immediately sends the reminders due for that day.
                            </p>

                            <div className="reminder-send-time">
                                <h4>Daily Pull + Send Time</h4>
                                <p className="help-text">Set the one daily time when Promptly pulls the latest appointment changes and immediately sends all due reminders.</p>
                                <div className="time-input-group">
                                    <input
                                        type="time"
                                        value={reminderSendTime}
                                        onChange={(e) => {
                                            setReminderSendTime(e.target.value);
                                            setSaveSuccess(false);
                                        }}
                                        className="time-input"
                                    />
                                    <span className="time-display">
                                        Promptly will pull and send at {new Date(`2000-01-01T${reminderSendTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    </span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="reminderSenderPhone">
                                    Default Sender Phone
                                </label>
                                <input
                                    type="tel"
                                    id="reminderSenderPhone"
                                    value={reminderSenderPhone}
                                    onChange={(e) => {
                                        setReminderSenderPhone(e.target.value);
                                        setSaveSuccess(false);
                                    }}
                                    placeholder="5186003007"
                                />
                                <small>Used for automated appointment reminders when a per-appointment sender is not provided.</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="selectedReminderPromptId">
                                    Reminder Prompt Template
                                </label>
                                <select
                                    id="selectedReminderPromptId"
                                    value={selectedReminderPromptId}
                                    onChange={(e) => {
                                        setSelectedReminderPromptId(e.target.value);
                                        setSaveSuccess(false);
                                    }}
                                >
                                    <option value="">System Default Prompt</option>
                                    {servicePrompts.services.map((service) => (
                                        <option key={service.id} value={service.id}>
                                            {service.name}
                                        </option>
                                    ))}
                                </select>
                                <small>
                                    Reminders will use the selected prompt template every time. Choose "System Default Prompt" to use the shared default message instead.
                                </small>
                            </div>

                            <div className="appointment-sync-section">
                                <h4>Daily Reminder Run</h4>
                                <p className="help-text">Automatically pull upcoming appointments from FieldRoutes and send the reminders due at the configured daily time.</p>

                                <label className="checkbox-toggle sync-toggle">
                                    <input
                                        type="checkbox"
                                        checked={appointmentSyncEnabled}
                                        onChange={(e) => {
                                            setAppointmentSyncEnabled(e.target.checked);
                                            setSaveSuccess(false);
                                        }}
                                    />
                                    <span className="toggle-label">
                                        {appointmentSyncEnabled ? '✅ Daily Reminder Run Enabled' : '⏸️ Daily Reminder Run Disabled'}
                                    </span>
                                </label>

                                {appointmentSyncEnabled && (
                                    <div className="sync-time-config">
                                        <p className="sync-info">
                                            Staff can keep creating or editing appointments in the source system right up until <strong>{new Date(`2000-01-01T${reminderSendTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</strong>. At that moment, Promptly pulls the latest data and sends everything due for that day in one batch.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="appointment-sync-section">
                                <h4>Automatic Reminder Flow Creation</h4>
                                <p className="help-text">Allow sync and Refresh to create SendPlans automatically for upcoming appointments</p>

                                <label className="checkbox-toggle sync-toggle">
                                    <input
                                        type="checkbox"
                                        checked={sendPlanCreationEnabled}
                                        onChange={(e) => {
                                            setSendPlanCreationEnabled(e.target.checked);
                                            setSaveSuccess(false);
                                        }}
                                    />
                                    <span className="toggle-label">
                                        {sendPlanCreationEnabled ? '✅ SendPlan Creation Enabled' : '⏸️ SendPlan Creation Disabled'}
                                    </span>
                                </label>

                                <div className="sync-time-config">
                                    <p className="sync-info">
                                        When disabled, Promptly can still sync and refresh appointments, but it will not create reminder SendPlans automatically. Use the per-conversation trigger to build the reminder flow only for the appointments you want.
                                    </p>
                                </div>
                            </div>

                            <div className="ai-personalization-section">
                                <h4>AI Personalization</h4>
                                <p className="help-text">Reminder generation is always AI-personalized and weather-aware.</p>
                                <div className="ai-info-box">
                                    <p><strong>💡 How it works:</strong></p>
                                    <p>Every reminder uses AI with the technician&apos;s persona, the selected prompt template, and weather context when available. If conditions look like they could affect the visit, the reminder will naturally make RESCHEDULE the easy next step.</p>
                                </div>

                                <div className="max-chars-section">
                                    <h5>Message Character Limit</h5>
                                    <p className="help-text">Control SMS costs by limiting message length. SMS segments are 160 characters each.</p>
                                    <div className="char-limit-input-group">
                                        <input
                                            type="number"
                                            min="50"
                                            max="1600"
                                            value={maxMessageCharacters}
                                            onChange={(e) => {
                                                setMaxMessageCharacters(parseInt(e.target.value) || 250);
                                                setSaveSuccess(false);
                                            }}
                                            className="char-limit-input"
                                        />
                                        <span className="char-limit-label">characters max</span>
                                    </div>
                                    <div className="char-limit-presets">
                                        <button type="button" className={`preset-btn ${maxMessageCharacters === 160 ? 'active' : ''}`} onClick={() => { setMaxMessageCharacters(160); setSaveSuccess(false); }}>160 (1 SMS)</button>
                                        <button type="button" className={`preset-btn ${maxMessageCharacters === 320 ? 'active' : ''}`} onClick={() => { setMaxMessageCharacters(320); setSaveSuccess(false); }}>320 (2 SMS)</button>
                                        <button type="button" className={`preset-btn ${maxMessageCharacters === 480 ? 'active' : ''}`} onClick={() => { setMaxMessageCharacters(480); setSaveSuccess(false); }}>480 (3 SMS)</button>
                                    </div>
                                    <p className="cost-hint">💰 Lower limits = lower Twilio costs. Current: ~{Math.ceil(maxMessageCharacters / 160)} SMS segment{Math.ceil(maxMessageCharacters / 160) > 1 ? 's' : ''} per message</p>
                                </div>
                            </div>

                            <div className="reminder-schedules">
                                {reminderSchedules.map((schedule) => (
                                    <div key={schedule.id} className={`reminder-schedule-item ${!schedule.enabled ? 'disabled' : ''}`}>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={schedule.enabled}
                                                onChange={(e) => updateReminderSchedule(schedule.id, 'enabled', e.target.checked)}
                                            />
                                            <span>Enabled</span>
                                        </label>

                                        <div className="schedule-inputs">
                                            <input
                                                type="number"
                                                min="1"
                                                max="365"
                                                value={schedule.value}
                                                onChange={(e) => updateReminderSchedule(schedule.id, 'value', parseInt(e.target.value) || 1)}
                                                className="schedule-value-input"
                                            />

                                            <span className="char-limit-label">day{schedule.value === 1 ? '' : 's'} before</span>

                                            {reminderSchedules.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeReminderSchedule(schedule.id)}
                                                    className="btn btn--danger btn--small"
                                                >
                                                    <FaTrash />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addReminderSchedule}
                                    className="btn btn--secondary btn--small"
                                >
                                    <FaPlus /> Add Another Reminder
                                </button>
                            </div>

                            <div className="preview-box">
                                <h4>Active Reminders:</h4>
                                <ul>
                                    {reminderSchedules.filter(s => s.enabled).map((schedule) => (
                                        <li key={schedule.id}>
                                            Send reminder <strong>{schedule.value} day{schedule.value === 1 ? '' : 's'}</strong> before appointment
                                        </li>
                                    ))}
                                </ul>
                                {reminderSchedules.filter(s => s.enabled).length === 0 && (
                                    <p><em>No reminders configured</em></p>
                                )}
                                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                                    <strong>Example:</strong> If an appointment is on 12/25/2024 and you configure "1 day before" with a daily pull/send time of 2:00 PM, Promptly will pull the latest appointments on 12/24/2024 at 2:00 PM and immediately send that reminder then.
                                </p>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn--secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn--primary"
                                    onClick={handleSaveConfiguration}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <LoadingSpinner size="small" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave />
                                            Save Configuration
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Service Prompts Tab */}
                    {activeTab === 'prompts' && (
                        <div className="config-section">
                            <h3>Service Prompt Configuration</h3>
                            <p className="section-description">
                                Create custom prompts for each service. Use <strong>{`{service}`}</strong>, <strong>{`{date}`}</strong>, and <strong>{`{time}`}</strong> as placeholders in your prompts.
                            </p>

                            {/* System Default Prompt */}
                            <div className="system-default-prompt">
                                <h4>System Default Prompt</h4>
                                <p className="help-text">This prompt is used when "System Default Prompt" is selected as the reminder template.</p>
                                <textarea
                                    value={servicePrompts.systemDefault}
                                    onChange={(e) => updateSystemDefault(e.target.value)}
                                    placeholder="Enter your default reminder prompt..."
                                    rows={3}
                                    className="prompt-textarea"
                                />
                            </div>

                            {/* Service-Specific Prompts */}
                            <div className="service-prompts-list">
                                <h4>Service-Specific Prompts</h4>
                                {servicePrompts.services.map((service) => (
                                    <div key={service.id} className="service-prompt-item">
                                        <div className="service-prompt-header">
                                            <input
                                                type="text"
                                                value={service.name}
                                                onChange={(e) => updateServicePrompt(service.id, 'name', e.target.value)}
                                                placeholder="Service name (e.g., Pest Control)"
                                                className="service-name-input"
                                            />
                                            {servicePrompts.services.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeServicePrompt(service.id)}
                                                    className="btn btn--danger btn--small"
                                                >
                                                    <FaTrash />
                                                </button>
                                            )}
                                        </div>
                                        <textarea
                                            value={service.prompt}
                                            onChange={(e) => updateServicePrompt(service.id, 'prompt', e.target.value)}
                                            placeholder="Enter service-specific prompt..."
                                            rows={2}
                                            className="prompt-textarea"
                                        />
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addServicePrompt}
                                    className="btn btn--secondary btn--small"
                                >
                                    <FaPlus /> Add Service Prompt
                                </button>
                            </div>

                            {/* Preview */}
                            <div className="preview-box">
                                <h4>Available Placeholders:</h4>
                                <ul>
                                    <li><code>{`{service}`}</code> - Service name (e.g., "Pest Control")</li>
                                    <li><code>{`{date}`}</code> - Appointment date (e.g., "Monday, November 18, 2025")</li>
                                    <li><code>{`{time}`}</code> - Appointment time (e.g., "2:00 PM")</li>
                                </ul>
                                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                                    <strong>Example:</strong> "Reminder: Your {`{service}`} appointment is scheduled for {`{date}`} at {`{time}`}."
                                </p>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn--secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn--primary"
                                    onClick={handleSaveServicePrompts}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <LoadingSpinner size="small" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave />
                                            Save Service Prompts
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Test Messaging Tab */}
                    {activeTab === 'test' && (
                        <div className="test-section">
                            {result && (
                                <div className="conversation-view">
                                    <div className="appointment-info">
                                        <h3>🧪 Full Flow Test Scheduled</h3>
                                        <div className="appointment-details">
                                            <p><strong>Appointment ID:</strong> {result.appointmentId}</p>
                                            <p><strong>Conversation ID:</strong> {result.conversationId}</p>
                                            <p><strong>SendPlan ID:</strong> {result.sendPlanId}</p>
                                            <p><strong>Service:</strong> {result.serviceDescription}</p>
                                            <p><strong>Phone:</strong> {result.phone}</p>
                                            <p><strong>Sender:</strong> {result.senderPhone}</p>
                                        </div>
                                    </div>

                                    <div className="conversation-messages">
                                        <h4>⏰ Reminder Scheduled for Immediate Send!</h4>
                                        <div className="success-info">
                                            <p>✅ Test appointment created (for tomorrow at 2:00 PM)</p>
                                            <p>✅ Conversation created and linked</p>
                                            <p>✅ Reminder scheduled for <strong>NOW</strong></p>
                                            <div className="info-box pending-send">
                                                <p><strong>📬 What happens next:</strong></p>
                                                <p>The background processor runs every 60 seconds.</p>
                                                <p style={{ marginTop: '0.5rem' }}>Your reminder will be sent within the next minute using:</p>
                                                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                                                    <li>Your AI Personalization settings</li>
                                                    <li>Your Max Character Limit ({result.serviceDescription ? 'configured' : '250 default'})</li>
                                                    <li>Weather data for the location</li>
                                                    <li>Service: <strong>{result.serviceDescription}</strong></li>
                                                </ul>
                                                <p style={{ marginTop: '0.75rem', color: '#059669', fontWeight: 500 }}>
                                                    📱 Check your phone in ~60 seconds!
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        className="btn btn--primary"
                                        onClick={() => setResult(null)}
                                    >
                                        Run Another Test
                                    </button>
                                    <button
                                        className="btn btn--secondary"
                                        onClick={requestCleanupTestData}
                                        disabled={cleanupLoading || loading}
                                        style={{ marginTop: '0.75rem' }}
                                    >
                                        {cleanupLoading ? 'Deleting Test Data...' : 'Delete Test Conversations'}
                                    </button>
                                </div>
                            )}

                            {!result && (
                                <>
                                    {cleanupResult && (
                                        <div className="success-message">
                                            <p>
                                                ✅ Cleanup complete: deleted {cleanupResult.conversationsDeleted} conversation(s), {cleanupResult.appointmentsDeleted} appointment(s), and {cleanupResult.sendPlansDeleted} send plan(s).
                                            </p>
                                        </div>
                                    )}

                                    <form onSubmit={handleTestSubmit} className="test-form">
                                        <div className="form-group">
                                            <label htmlFor="recipientFirstName">
                                                First Name <span className="required">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="recipientFirstName"
                                                name="recipientFirstName"
                                                value={testFormData.recipientFirstName}
                                                onChange={handleTestFormChange}
                                                placeholder="Enter first name"
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="recipientLastName">
                                                Last Name
                                            </label>
                                            <input
                                                type="text"
                                                id="recipientLastName"
                                                name="recipientLastName"
                                                value={testFormData.recipientLastName}
                                                onChange={handleTestFormChange}
                                                placeholder="Enter last name (Optional)"
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="recipientPhone">
                                                Recipient Phone <span className="required">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                id="recipientPhone"
                                                name="recipientPhone"
                                                value={testFormData.recipientPhone}
                                                onChange={handleTestFormChange}
                                                placeholder="5189193221"
                                                disabled={loading}
                                            />
                                            <small>Test number: 518-919-3221 (Incoming Text Test)</small>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="city">
                                                    City <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="city"
                                                    name="city"
                                                    value={testFormData.city}
                                                    onChange={handleTestFormChange}
                                                    placeholder="Albany"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="state">
                                                    State <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="state"
                                                    name="state"
                                                    value={testFormData.state}
                                                    onChange={handleTestFormChange}
                                                    placeholder="NY"
                                                    maxLength="2"
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="zipCode">
                                                    ZIP Code <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="zipCode"
                                                    name="zipCode"
                                                    value={testFormData.zipCode}
                                                    onChange={handleTestFormChange}
                                                    placeholder="12203"
                                                    maxLength="5"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <small className="location-help">📍 Location is used for weather-aware reminders</small>

                                        <div className="form-group">
                                            <label htmlFor="serviceId">
                                                Service <span className="required">*</span>
                                            </label>
                                            <select
                                                id="serviceId"
                                                name="serviceId"
                                                value={testFormData.serviceId}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Check if it's a FieldRoutes service (prefixed with 'fr_')
                                                    if (value.startsWith('fr_')) {
                                                        const frService = fieldroutesServiceTypes.find(s => `fr_${s.typeId}` === value);
                                                        setTestFormData(prev => ({
                                                            ...prev,
                                                            serviceId: value,
                                                            customServiceDescription: frService?.description || ''
                                                        }));
                                                    } else {
                                                        setTestFormData(prev => ({
                                                            ...prev,
                                                            serviceId: value,
                                                            customServiceDescription: ''
                                                        }));
                                                    }
                                                    setError('');
                                                    setResult(null);
                                                }}
                                                disabled={loading}
                                                className="service-select"
                                            >
                                                <option value="">-- Select Service --</option>
                                                {fieldroutesServiceTypes.length > 0 && (
                                                    <optgroup label="FieldRoutes Services">
                                                        {fieldroutesServiceTypes.map((service) => (
                                                            <option key={`fr_${service.typeId}`} value={`fr_${service.typeId}`}>
                                                                {service.description} {service.frequency ? `(${service.frequency})` : ''}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                                <optgroup label="Custom Services">
                                                    {servicePrompts.services.map((service) => (
                                                        <option key={service.id} value={service.id}>
                                                            {service.name || `Service ${service.id}`}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            </select>
                                            <small>Choose a FieldRoutes service or custom service</small>
                                        </div>

                                        <div className="form-group custom-service-group">
                                            <label htmlFor="customServiceDescription">
                                                Custom Service Description <span className="optional">(optional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="customServiceDescription"
                                                name="customServiceDescription"
                                                value={testFormData.customServiceDescription}
                                                onChange={handleTestFormChange}
                                                placeholder="e.g., Quarterly Pest Control, Lawn Fertilization"
                                                disabled={loading}
                                            />
                                            <small>Override service name to test with FieldRoutes-style service descriptions</small>
                                        </div>

                                        <div className="form-actions">
                                            <button
                                                type="button"
                                                className="btn btn--secondary"
                                                onClick={() => setActiveTab('reminders')}
                                                disabled={loading || cleanupLoading}
                                            >
                                                Back to Settings
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn--secondary"
                                                onClick={requestCleanupTestData}
                                                disabled={loading || cleanupLoading}
                                            >
                                                {cleanupLoading ? 'Deleting Test Data...' : 'Delete Test Conversations'}
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn--primary"
                                                disabled={loading || cleanupLoading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <LoadingSpinner size="small" />
                                                        Scheduling...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaPaperPlane />
                                                        Test Full Reminder Flow
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>

                                    <div className="info-box">
                                        <h4>🧪 What this test does:</h4>
                                        <ol>
                                            <li>Creates a test appointment for <strong>tomorrow at 2:00 PM</strong></li>
                                            <li>Creates a conversation linked to the appointment</li>
                                            <li>Schedules a reminder for <strong>immediate send</strong></li>
                                            <li>Background processor picks it up within 60 seconds</li>
                                            <li>Message is sent using your <strong>AI Personalization</strong> and <strong>Max Character</strong> settings</li>
                                        </ol>
                                        <p style={{ marginTop: '1rem', fontSize: '0.813rem', color: 'var(--color-text-muted)' }}>
                                            <strong>This tests the complete reminder flow</strong> - the same flow that runs when appointments are synced from FieldRoutes.
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default ConfigurationModal;
